import { Component, computed, inject, signal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import {FormControl, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatIcon} from "@angular/material/icon";
import {NgFor, NgIf} from "@angular/common";
import { firstValueFrom } from 'rxjs';
import { TwoFactorMedium, TwoFactorService } from '@core/service/two-factor.service';
import { HttpMesageHandlerService } from '@core/service/http-mesage-handler';

@Component({
  selector: 'app-two-fa-dialog',
  templateUrl: './two-fa-dialog.component.html',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatIcon,
    NgIf,
    NgFor
  ],
  styleUrls: ['./two-fa-dialog.component.scss']
})
export class TwoFaDialogComponent {
  private ref = inject(MatDialogRef<TwoFaDialogComponent>);
  private twoFactor = inject(TwoFactorService);
  private httpMsgHandler = inject(HttpMesageHandlerService);

  code = new FormControl('', [Validators.required, Validators.pattern(/^\d{6}$/)]);
  selectedMediumId = new FormControl<number | null>(null);
  rememberMedium = new FormControl(false, { nonNullable: true });
  loading = signal(false);
  loadingMediums = signal(false);
  mediums = signal<TwoFactorMedium[]>([]);
  showAlternateMethods = signal(false);
  readonly hasActiveMediums = computed(() => this.mediums().some((medium) => medium.activo));
  readonly dialogTitle = computed(() =>
    this.showAlternateMethods() ? 'ELEGIR METODO DE ENVIO' : 'VERIFICACION EN DOS PASOS'
  );
  readonly dialogDescription = computed(() =>
    this.showAlternateMethods()
      ? 'Selecciona como quieres recibir el siguiente codigo.'
      : 'Ingresa el codigo de 6 digitos enviado a tu medio registrado.'
  );

  onConfirm() {
    if (this.code.invalid) return;
    this.loading.set(true);
    this.ref.close(this.code.value);
  }

  onCancel() {
    this.ref.close(null);
  }

  onOpenAlternateMethodsMouseDown(event: MouseEvent): void {
    event.preventDefault();
    void this.openAlternateMethods();
  }

  async openAlternateMethods(): Promise<void> {
    this.code.markAsPristine();
    this.code.markAsUntouched();
    this.showAlternateMethods.set(true);

    if (!this.mediums().length && !this.loadingMediums()) {
      await this.loadMediums();
    }
  }

  backToCodeEntry(): void {
    this.code.markAsPristine();
    this.code.markAsUntouched();
    this.showAlternateMethods.set(false);
  }

  async onSendSelectedMedium() {
    const mediumId = this.selectedMediumId.value;
    const medium = this.mediums().find((item) => item.idMedioAutenticacion === mediumId);

    if (!mediumId || !medium?.activo) return;

    this.loading.set(true);
    try {
      if (this.rememberMedium.value) {
        await firstValueFrom(this.twoFactor.savePreferredMedium(mediumId));
      }

      await firstValueFrom(this.twoFactor.sendCode(mediumId));
      this.code.reset('');
      this.code.markAsUntouched();
      this.showAlternateMethods.set(false);
    } catch (error) {
      this.httpMsgHandler.handleMsg(error as any);
    } finally {
      this.loading.set(false);
    }
  }

  async onResendDefault() {
    this.loading.set(true);
    try {
      await firstValueFrom(this.twoFactor.sendCode());
      this.code.reset('');
      this.code.markAsUntouched();
      this.showAlternateMethods.set(false);
    } catch (error) {
      this.httpMsgHandler.handleMsg(error as any);
    } finally {
      this.loading.set(false);
    }
  }

  canSendSelectedMedium(): boolean {
    const mediumId = this.selectedMediumId.value;
    if (!mediumId) return false;

    return this.mediums().some(
      (medium) => medium.idMedioAutenticacion === mediumId && medium.activo
    );
  }

  trackByMediumId(_: number, medium: TwoFactorMedium): number {
    return medium.idMedioAutenticacion;
  }

  mediumKind(medium: TwoFactorMedium): 'whatsapp' | 'mail' | 'generic' {
    const description = (medium.descripcion ?? '').trim().toLowerCase();

    if (description.includes('whatsapp')) {
      return 'whatsapp';
    }

    if (
      description.includes('correo') ||
      description.includes('email') ||
      description.includes('mail')
    ) {
      return 'mail';
    }

    return 'generic';
  }

  public onOtpInput(event: Event): void {
    const input = event.target as HTMLInputElement;

    // 1) Solo digitos
    const onlyDigits = (input.value ?? '').replace(/[^0-9]/g, '').slice(0, 6);

    // 2) Reflejar en el input (por si el usuario pego texto con letras)
    input.value = onlyDigits;

    // 3) Setear en el FormControl
    this.code.setValue(onlyDigits);
    this.code.markAsTouched();
  }

  public otpChar(index: number): string {
    const v = (this.code.value ?? '').toString();
    return v[index] ?? '';
  }

  private async loadMediums(): Promise<void> {
    this.loadingMediums.set(true);

    try {
      const response = await firstValueFrom(this.twoFactor.getMediums());
      const mediums = Array.isArray(response?.data) ? response.data : [];
      this.mediums.set(mediums);

      const firstActiveMedium = mediums.find((medium) => medium.activo) ?? null;
      this.selectedMediumId.setValue(firstActiveMedium?.idMedioAutenticacion ?? null);
    } catch (error) {
      this.httpMsgHandler.handleMsg(error as any);
    } finally {
      this.loadingMediums.set(false);
    }
  }
}
